import type { LensCardData } from '~/types/leLens';

import { type LensDataReadiness, lensDataReady } from '~/utils/leLensTransport';

import { dateError, defaultDates, reportCalendar, reportFilters } from '../ContributionMargin/liveModel';

import { dailyFields, salesDailyFields, salesSummaryFields } from './batchFields';
import { completedMarketingRequest, transformMarketingCard } from './batches';
import { config } from './config';
import { attributeParam, currentPeriod, legacyAttributes } from './marketingControls';
import { type Filters, type Row, defaults, dimensions, metrics } from './presentationModel';
import { marketingDateError, marketingPrior, salesDateControls } from './workflows';

export const viewNames = ['Performance', 'Attribution', 'PoP', 'Sales', 'Monthly', 'Product mix'];
export function marketingRequiredCards(view: string): Array<string> {
  return view === 'Attribution'
    ? ['presentation_attribution']
    : view === 'Sales'
      ? ['sales_summary', 'presentation_sales_daily', 'sales_total']
      : view === 'Monthly'
        ? ['monthly']
        : ['summary', 'presentation_daily'];
}
export const filterProjection: Record<string, string> = {
  attribution: 'attribution_model',
};
const nativeViews: Record<string, string> = {
  country: 'Performance',
  campaign: 'Performance',
  adgroup: 'Performance',
  segment: 'Performance',
  performance: 'Performance',
  attribution: 'Attribution',
  pop: 'PoP',
  sales: 'Sales',
  monthly: 'Monthly',
};
export type MarketingScope = ReturnType<typeof marketingScope>;
export type MarketingPresentation = {
  daily: Array<Row>;
  summary: Array<Row>;
  attribution: Array<Row>;
  previous: Array<Row>;
  summaryPrevious: Array<Row>;
  sales: Array<Row>;
  salesDaily: Array<Row>;
  salesTotal: Array<Row>;
  salesPreviousTotal: Array<Row>;
  monthly: Array<Row>;
  asOf: string;
  defaultDates: { from: string; to: string };
  identity?: string;
  sourceScope: string;
  salesPrevious: Array<Row>;
  salesPreviousDaily: Array<Row>;
  chartMetric: string;
  secondMetric: string;
  firstRelative: boolean;
  secondRelative: boolean;
};
export const salesViewDefaults = { split: 'traffic_channel', split2: 'All', cadence: 'Weekly' };
const monthlyFrom = (to: string) => String(Number(to.slice(0, 4)) - 2) + '-01-01';
export function marketingScope(params: URLSearchParams, now: Date, theme = 'light') {
  const dates = defaultDates(now),
    filters: Filters = { ...defaults, ...dates, theme };
  for (const key of Object.keys(defaults) as Array<keyof Filters>) {
    const value = params.get('pm_' + key);
    if (value !== null) {
      filters[key] = value;
    }
  }
  // Current native aliases remain usable; the yesterday pm_ namespace wins explicitly.
  if (!params.has('pm_view')) {
    filters.view = nativeViews[params.get('reportView') ?? params.get('view') ?? ''] ?? filters.view;
  }
  for (const key of ['from', 'to'] as const) {
    if (!params.has('pm_' + key) && params.has(key)) {
      filters[key] = params.get(key)!;
    }
  }
  for (const [key, native] of Object.entries(filterProjection)) {
    if (!params.has('pm_' + key) && params.has(native)) {
      const values = params.getAll(native);
      if (values.length === 1) {
        filters[key as keyof Filters] = values[0];
      }
    }
  }
  const aliases = { split: 'data_split_1', split2: 'data_split_2', cadence: 'date_grain' } as const;
  for (const [key, native] of Object.entries(aliases)) {
    if (!params.has('pm_' + key) && params.has(native)) {
      filters[key as keyof Filters] = params.get(native)!;
    }
  }
  if (!params.has('pm_timeSplit') && params.get('tableGrain') === 'cadence') {
    filters.timeSplit = 'Date Cadence';
  }
  const drillSplits: Record<string, string> = {
    country: 'country',
    campaign: 'campaign_name',
    adgroup: 'adgroup_name',
    segment: 'campaign_segment_perf_marketing',
  };
  if (!params.has('pm_split') && !params.has('data_split_1') && drillSplits[params.get('reportView') ?? '']) {
    filters.split = drillSplits[params.get('reportView')];
  }
  if (filters.view === 'Sales') {
    if (!params.has('pm_split') && !params.has('data_split_1')) {
      filters.split = 'traffic_channel';
    }
    if (!params.has('pm_cadence') && !params.has('date_grain')) {
      filters.cadence = 'Weekly';
    }
  }
  if (filters.view === 'Monthly') {
    filters.attribution = config.defaultModel!;
  }
  const requested = params.get('comparison') ?? 'none';
  const comparison = filters.view !== 'PoP' ? 'none' : requested === 'none' ? 'year' : requested;
  Object.assign(filters, currentPeriod(filters.from, filters.to, comparison));
  if (filters.view === 'Monthly') {
    filters.from = monthlyFrom(filters.to);
  }
  const selections: Record<string, Array<string>> = {};
  for (const key of config.filters) {
    if (!['data_split_1', 'data_split_2', 'date_grain'].includes(key)) {
      const values = params.getAll(attributeParam(filters.view, key));
      if (values.length) {
        selections[key] = values;
      }
    }
  }
  for (const [key, native] of Object.entries(legacyAttributes)) {
    const legacy = attributeParam(filters.view, native) === native ? params.get('pm_' + key) : null;
    if (!selections[native] && legacy && legacy !== 'All') {
      selections[native] = [legacy];
    }
    filters[key as keyof Filters] = selections[native]?.join(', ') ?? 'All';
  }
  const applied = filters.view === 'Sales' ? config.viewFilters.sales : config.viewFilters.performance;
  const attributes = Object.fromEntries(
    Object.entries(selections).filter(
      ([key]) => applied.includes(key) && key !== 'attribution_model' && !(key in salesDateControls),
    ),
  );
  for (const [key, native] of Object.entries(filterProjection)) {
    const value = filters[key as keyof Filters];
    if (value !== 'All') {
      selections[native] = [value];
    } else {
      delete selections[native];
    }
  }
  selections.data_split_1 = [filters.split];
  selections.data_split_2 = [filters.split2];
  selections.date_grain = [filters.cadence];
  const request = reportFilters(filters.from, filters.to, selections, comparison);
  if (comparison !== 'none') {
    request.period = {
      window: { from: filters.from, to: filters.to },
      comparison: marketingPrior(
        filters.from,
        filters.to,
        comparison,
        params.get('priorFrom') ?? '',
        params.get('priorTo') ?? '',
      ),
    };
  }
  const projected = completedMarketingRequest(request, now);
  let problem = dateError(filters.from, filters.to) ?? undefined;
  if (filters.to > dates.to) {
    problem = 'Choose completed Sydney dates, before today.';
  }
  if (!viewNames.includes(filters.view) || !metrics[filters.metric]) {
    problem = 'Choose a supported report view and metric.';
  }
  const splits = filters.view === 'Sales' ? config.splitDimensions.sales : config.splitDimensions.media;
  if (![filters.split, filters.split2].every((split) => splits.includes(split))) {
    problem = 'Choose supported source split dimensions.';
  }
  if (
    !['Daily', 'Weekly', 'Monthly', 'Annually'].includes(filters.cadence) ||
    !['Full Period', 'Date Cadence'].includes(filters.timeSplit)
  ) {
    problem = 'Choose a supported cadence and time split.';
  }
  if (!['none', 'previous', '28days', 'year', 'custom'].includes(comparison)) {
    problem = 'Choose a supported comparison.';
  }
  if (filters.attribution === 'All' || !filters.attribution) {
    problem = 'Select one attribution model. Spend repeats across models.';
  }
  for (const key of config.filters) {
    if (params.getAll(attributeParam(filters.view, key)).some((value) => !value.trim())) {
      problem = 'Blank source selections cannot be represented safely; clear the filter instead.';
    }
  }
  for (const key of Object.values(filterProjection)) {
    if (params.getAll(key).length > 1) {
      problem = 'The saved single-select control cannot represent multiple ' + key + ' selections.';
    }
  }
  problem = marketingDateError(params, filters.view === 'Sales', comparison) ?? problem;
  // Freeze the real source cutoff separately from the browser's calendar.
  const chartMetric = params.get('chartMetric') || (filters.view === 'Sales' ? 'gm_br' : 'gm');
  const secondMetric = params.get('secondMetric') || (filters.view === 'Sales' ? 'gm_br' : 'cost');
  const available = new Set([
    ...dailyFields.slice(2),
    ...salesDailyFields.slice(2),
    ...Object.keys(config.ratios),
    ...Object.keys(config.derived ?? {}),
  ]);
  if (![chartMetric, secondMetric].every((metric) => available.has(metric))) {
    problem = 'Choose supported source chart metrics.';
  }
  if (
    ['firstMode', 'secondMode'].some((key) => params.has(key) && !['absolute', 'relative'].includes(params.get(key)))
  ) {
    problem = 'Choose absolute or relative chart modes.';
  }
  return {
    filters,
    attributes,
    request: projected,
    problem,
    comparison,
    dates,
    asOf: reportCalendar(now).today,
    chartMetric,
    secondMetric,
    firstRelative: params.get('firstMode') === 'relative',
    secondRelative: (params.get('secondMode') ?? (filters.view === 'Sales' ? 'relative' : 'absolute')) === 'relative',
  };
}
function decodeRows(
  rows: Array<Record<string, unknown>>,
  fields: Array<string>,
  stringFields: Array<string>,
): Array<Row> {
  return rows.map((row) =>
    Object.fromEntries(
      fields.map((key) => {
        const value = row[key];
        if (stringFields.includes(key)) {
          if (typeof value !== 'string') {
            throw new Error('Missing source dimension ' + key + '.');
          }
          if (['bucket', 'p_date'].includes(key) && dateError(value, value)) {
            throw new Error('Invalid source calendar bucket.');
          }
        } else if (value !== null && (typeof value !== 'number' || !Number.isFinite(value))) {
          throw new Error('Invalid source operand ' + key + '.');
        }
        return [key, value as string | number | null];
      }),
    ),
  );
}
function decodeBatches(
  card: LensCardData,
  fields: Array<string>,
  stringFields: Array<string>,
  prior = false,
): Array<Row> {
  const rows = prior ? card.comparison?.rows : card.rows;
  if (!rows?.length) {
    return [];
  }
  const seen = new Set<number>();
  let total: number | undefined;
  const records: Array<Record<string, unknown>> = [];
  for (const row of rows) {
    const { batch, record_count: count, source_count: source, payload } = row;
    if (
      typeof batch !== 'number' ||
      !Number.isSafeInteger(batch) ||
      batch < 0 ||
      seen.has(batch) ||
      typeof count !== 'number' ||
      !Number.isSafeInteger(count) ||
      count < 1 ||
      typeof source !== 'number' ||
      !Number.isSafeInteger(source) ||
      source < count ||
      (total !== undefined && source !== total) ||
      typeof payload !== 'string'
    ) {
      throw new Error('Invalid PM presentation batch.');
    }
    seen.add(batch);
    total = source;
    const values: unknown = JSON.parse(payload);
    if (!Array.isArray(values) || values.length !== count) {
      throw new Error('Incomplete PM presentation batch.');
    }
    for (const value of values) {
      if (!Array.isArray(value) || value.length !== fields.length) {
        throw new Error('Invalid PM presentation tuple.');
      }
      records.push(Object.fromEntries(fields.map((field, index) => [field, value[index]])));
    }
  }
  if (records.length !== total || [...seen].some((batch) => batch >= seen.size)) {
    throw new Error('Incomplete PM presentation population.');
  }
  return decodeRows(records, fields, stringFields);
}
export function adaptMarketing(
  scope: MarketingScope,
  result: LensDataReadiness,
): { ready: boolean; model: MarketingPresentation; problem?: string } {
  const model: MarketingPresentation = {
    sourceScope: JSON.stringify(scope.request),
    chartMetric: scope.chartMetric,
    secondMetric: scope.secondMetric,
    firstRelative: scope.firstRelative,
    secondRelative: scope.secondRelative,
    salesPrevious: [],
    salesPreviousDaily: [],
    daily: [],
    summary: [],
    summaryPrevious: [],
    attribution: [],
    previous: [],
    sales: [],
    salesDaily: [],
    salesTotal: [],
    salesPreviousTotal: [],
    monthly: [],
    asOf: scope.asOf,
    defaultDates: scope.dates,
  };
  const view = scope.filters.view;
  const required = marketingRequiredCards(view);
  const prior = scope.comparison !== 'none' ? required : [];
  const cards = (result.cards ?? []).map(transformMarketingCard);
  if (!lensDataReady({ ...result, cards }, required, !scope.problem, prior)) {
    return {
      ready: false,
      model,
      problem:
        scope.problem ?? cards.find((card) => required.includes(card.cardId) && card.status === 'error')?.message,
    };
  }
  const card = (id: string) => cards.find((value) => value.cardId === id);
  try {
    if (view === 'Attribution') {
      const dims = ['attribution_model', 'traffic_channel', 'country'];
      model.attribution = decodeBatches(card('presentation_attribution'), [...dims, ...dailyFields.slice(2)], dims);
    } else if (view === 'Sales') {
      model.sales = decodeRows(card('sales_summary').rows ?? [], salesSummaryFields, ['split_one', 'split_two']);
      model.salesDaily = decodeBatches(
        card('presentation_sales_daily'),
        ['bucket', 'split_one', 'split_two', ...salesDailyFields.slice(2)],
        ['bucket', 'split_one', 'split_two'],
      );
      model.salesTotal = decodeRows(card('sales_total').rows ?? [], salesDailyFields.slice(2), []);
      if (prior.length) {
        model.salesPrevious = decodeRows(card('sales_summary').comparison?.rows ?? [], salesSummaryFields, [
          'split_one',
          'split_two',
        ]);
        model.salesPreviousDaily = decodeBatches(
          card('presentation_sales_daily'),
          ['bucket', 'split_one', 'split_two', ...salesDailyFields.slice(2)],
          ['bucket', 'split_one', 'split_two'],
          true,
        );
        model.salesPreviousTotal = decodeRows(
          card('sales_total').comparison?.rows ?? [],
          salesDailyFields.slice(2),
          [],
        );
      }
      if (model.salesTotal.length > 1 || model.salesPreviousTotal.length > 1) {
        throw new Error('Ambiguous Sales global distinct total.');
      }
    } else if (view === 'Monthly') {
      model.monthly = decodeRows(card('monthly').rows ?? [], ['p_date', ...dailyFields.slice(2)], ['p_date']);
    } else {
      model.summary = decodeRows(
        card('summary').rows ?? [],
        ['split_one', 'split_two', ...dailyFields.slice(2)],
        ['split_one', 'split_two'],
      ).map((row) => ({
        ...row,
        p_date: scope.filters.from,
        [scope.filters.split]: row.split_one,
        ...(scope.filters.split2 === scope.filters.split ? {} : { [scope.filters.split2]: row.split_two }),
      }));
      const fields = ['bucket', 'split_one', 'split_two', ...dailyFields.slice(2)];
      const map = (row: Row): Row => ({
        ...row,
        p_date: row.bucket,
        [scope.filters.split]: row.split_one,
        ...(scope.filters.split2 === scope.filters.split ? {} : { [scope.filters.split2]: row.split_two }),
      });
      model.daily = decodeBatches(card('presentation_daily'), fields, fields.slice(0, 3)).map(map);
      if (prior.length) {
        model.previous = decodeBatches(card('presentation_daily'), fields, fields.slice(0, 3), true).map(map);
        model.summaryPrevious = decodeRows(
          card('summary').comparison?.rows ?? [],
          ['split_one', 'split_two', ...dailyFields.slice(2)],
          ['split_one', 'split_two'],
        );
      }
    }
    model.identity = result.responseIdentity;
    return { ready: true, model };
  } catch (error) {
    return {
      ready: false,
      model: {
        ...model,
        daily: [],
        summary: [],
        attribution: [],
        previous: [],
        summaryPrevious: [],
        sales: [],
        salesDaily: [],
        salesTotal: [],
        salesPreviousTotal: [],
        monthly: [],
      },
      problem: error instanceof Error ? error.message : 'Invalid PM source result.',
    };
  }
}
export function marketingURL(params: URLSearchParams, changes: Partial<Filters>): URLSearchParams {
  const adjusted = changes.view === 'Sales' ? { ...salesViewDefaults, ...changes } : changes;
  const next = new URLSearchParams(params);
  for (const [key, value] of Object.entries(adjusted)) {
    next.set('pm_' + key, value);
    if (filterProjection[key]) {
      next.delete(filterProjection[key]);
    }
    if (key === 'from' || key === 'to') {
      next.delete(key);
    }
    if (key === 'view') {
      next.delete('reportView');
      next.delete('view');
    }
    if (key === 'split') {
      next.delete('data_split_1');
    }
    if (key === 'split2') {
      next.delete('data_split_2');
    }
    if (key === 'cadence') {
      next.delete('date_grain');
    }
  }
  if ('split' in adjusted) {
    next.set('pm_highlight', '');
  }
  if (changes.view === 'Sales' || changes.view === 'Monthly') {
    for (const key of ['chartMetric', 'secondMetric', 'firstMode', 'secondMode', 'tableGrain']) {
      next.delete(key);
    }
  }
  if (changes.view === 'Sales') {
    for (const key of ['bod_sales', 'mweb_sales']) {
      if (!next.has(key)) {
        next.set(key, 'Other Sale');
      }
    }
  }
  return next;
}
export const dimensionTitle = (key: string) => dimensions[key] ?? key.replace(/_/g, ' ');

/** Native attributes retain every selection; no array is collapsed to a comma-delimited member. */
export function marketingSourceURL(params: URLSearchParams, changes: Record<string, string | Array<string> | null>) {
  const next = new URLSearchParams(params);
  for (const [key, value] of Object.entries(changes)) {
    next.delete(key);
    for (const member of Array.isArray(value) ? value : value ? [value] : []) {
      next.append(key, member);
    }
  }
  return next;
}

/** A host edit is one transition: explicit fields override view defaults regardless of key order. */
export function marketingBridgeURL(
  params: URLSearchParams,
  changes: Record<string, unknown>,
  multiple: ReadonlySet<string> = new Set(),
): URLSearchParams {
  const report: Partial<Filters> = {},
    source: Record<string, string | Array<string> | null> = {};
  for (const [key, value] of Object.entries(changes)) {
    if (key in defaults && !multiple.has(key)) {
      if (value !== null && typeof value !== 'string') {
        throw Error('Invalid report edit value.');
      }
      report[key as keyof Filters] = typeof value === 'string' ? value : '';
    } else {
      if (
        value !== null &&
        typeof value !== 'string' &&
        (!Array.isArray(value) || !value.every((v) => typeof v === 'string'))
      ) {
        throw Error('Invalid source edit value.');
      }
      source[key] =
        typeof value === 'string' && multiple.has(key)
          ? value
              .split(',')
              .map((member) => member.trim())
              .filter(Boolean)
          : (value as string | Array<string> | null);
    }
  }
  return marketingSourceURL(marketingURL(params, report), source);
}
